import React, { useEffect, useState, useCallback } from 'react';
import { Zap, Shield, UserCheck, UserX, ChevronDown, ChevronRight, Plus, Trash2, Copy, Eye, EyeOff, Star, Building2, GraduationCap, Clock, Key, RefreshCw, Check, X, Search } from 'lucide-react';
import clsx from 'clsx';
import toast from 'react-hot-toast';
import {
  getScreeningBrief, getScreeningRules, createScreeningRule, updateScreeningRule, deleteScreeningRule,
  promoteCandidate, rejectScreening, confirmStaged, dismissStaged, getScreeningDefaults, getApiKeys, createApiKey, deleteApiKey, getPositions
} from '../api';
import { User, Position } from '../types';
import MailAccounts from './MailAccounts';

interface Props {
  currentUser: User;
  notificationBell?: React.ReactNode;
  onOpenCandidate?: (id: string) => void;
}

export default function BossBrief({ currentUser, notificationBell, onOpenCandidate }: Props) {
  const isHr = currentUser.role === 'admin' || currentUser.role === 'hr' || currentUser.role === 'vip';

  // Brief data
  const [brief, setBrief] = useState<any>(null);
  const [loading, setLoading] = useState(true);

  // Rules
  const [rules, setRules] = useState<any[]>([]);
  const [positions, setPositions] = useState<Position[]>([]);
  const [defaults, setDefaults] = useState<any>(null);

  // API Keys
  const [apiKeys, setApiKeys] = useState<any[]>([]);
  const [showApiKeys, setShowApiKeys] = useState(false);
  const [newKeyName, setNewKeyName] = useState('');
  const [generatedKey, setGeneratedKey] = useState('');

  // Headhunter tokens
  const [hhTokens, setHhTokens] = useState<any[]>([]);
  const [newHhName, setNewHhName] = useState('');
  const [newHhLink, setNewHhLink] = useState('');

  // Rule editor
  const [editingRule, setEditingRule] = useState<any>(null);
  const [showRuleForm, setShowRuleForm] = useState(false);
  const [ruleForm, setRuleForm] = useState({
    position_id: '' as string,
    rule_name: '',
    min_education: '本科',
    target_companies: '' as string,
    target_schools: '' as string,
    min_work_years: 0,
    required_keywords: '' as string,
  });

  // Tabs
  const [activeTab, setActiveTab] = useState<'staged' | 'overview' | 'pending' | 'rules' | 'settings'>('staged');
  const [pendingSearch, setPendingSearch] = useState('');
  const [stagedSearch, setStagedSearch] = useState('');
  const [selectedStaged, setSelectedStaged] = useState<Set<string>>(new Set());

  // Sections collapsed
  const [sectionsOpen, setSectionsOpen] = useState({ today: true, period: true, positions: true });

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const [briefData, rulesData, posData] = await Promise.all([
        getScreeningBrief(7),
        getScreeningRules(),
        getPositions(),
      ]);
      setBrief(briefData);
      setRules(rulesData);
      setPositions(posData);
    } catch (err) {
      console.error('Fetch boss brief error:', err);
    }
    setLoading(false);
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const fetchApiKeys = useCallback(async () => {
    try { setApiKeys(await getApiKeys()); } catch {}
  }, []);

  const fetchDefaults = useCallback(async () => {
    try { setDefaults(await getScreeningDefaults()); } catch {}
  }, []);

  const fetchHhTokens = useCallback(async () => {
    try {
      const res = await fetch('/api/headhunter/admin/tokens', { credentials: 'include' });
      if (res.ok) setHhTokens(await res.json());
    } catch {}
  }, []);

  useEffect(() => {
    if (activeTab === 'settings' && isHr) { fetchApiKeys(); fetchHhTokens(); }
    if (activeTab === 'rules') { fetchDefaults(); }
  }, [activeTab, isHr, fetchApiKeys, fetchDefaults, fetchHhTokens]);

  // ─── Actions ──────────────────────────────────────────────────

  const handlePromote = async (id: string) => {
    try { await promoteCandidate(id); toast.success('已放行到人才池'); fetchAll(); }
    catch (err: any) { toast.error(err.response?.data?.message || '操作失败'); }
  };

  const handleReject = async (id: string) => {
    if (!window.confirm('确认淘汰此候选人？')) return;
    try { await rejectScreening(id); toast.success('已淘汰'); fetchAll(); }
    catch (err: any) { toast.error(err.response?.data?.message || '操作失败'); }
  };

  const handleConfirmStaged = async (ids: string[]) => {
    if (ids.length === 0) return;
    try {
      const res = await confirmStaged(ids);
      toast.success(`已确认 ${res.confirmed} 名候选人导入人才池`);
      setSelectedStaged(new Set());
      fetchAll();
    } catch (err: any) { toast.error(err.response?.data?.message || '操作失败'); }
  };

  const handleDismissStaged = async (ids: string[]) => {
    if (ids.length === 0) return;
    if (!window.confirm(`确认跳过 ${ids.length} 名候选人？`)) return;
    try {
      const res = await dismissStaged(ids);
      toast.success(`已跳过 ${res.dismissed} 名候选人`);
      setSelectedStaged(new Set());
      fetchAll();
    } catch (err: any) { toast.error(err.response?.data?.message || '操作失败'); }
  };

  const toggleStagedSelection = (id: string) => {
    setSelectedStaged(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const toggleAllStaged = (candidates: any[]) => {
    if (selectedStaged.size === candidates.length) {
      setSelectedStaged(new Set());
    } else {
      setSelectedStaged(new Set(candidates.map((c: any) => c.id)));
    }
  };

  const handleCreateApiKey = async () => {
    try {
      const res = await createApiKey({ name: newKeyName || 'Boss直聘导入' });
      setGeneratedKey(res.key);
      setNewKeyName('');
      fetchApiKeys();
      toast.success('API密钥已创建');
    } catch (err: any) { toast.error(err.response?.data?.message || '创建失败'); }
  };

  const handleDeleteApiKey = async (id: string) => {
    if (!window.confirm('确认禁用此密钥？')) return;
    try { await deleteApiKey(id); fetchApiKeys(); toast.success('已禁用'); }
    catch (err: any) { toast.error('操作失败'); }
  };

  const handleCreateHhToken = async () => {
    if (!newHhName.trim()) { toast.error('请输入猎头名称'); return; }
    try {
      const res = await fetch('/api/headhunter/admin/tokens', {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: newHhName.trim() }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.message);
      setNewHhLink(`${window.location.origin}/headhunter/${data.token}`);
      setNewHhName('');
      fetchHhTokens();
      toast.success('猎头链接已生成');
    } catch (err: any) { toast.error(err.message || '创建失败'); }
  };

  const handleDeleteHhToken = async (id: string) => {
    if (!window.confirm('确认停用此猎头链接？')) return;
    try {
      await fetch(`/api/headhunter/admin/tokens/${id}`, { method: 'DELETE', credentials: 'include' });
      fetchHhTokens();
      toast.success('已停用');
    } catch { toast.error('操作失败'); }
  };

  const openRuleForm = (rule?: any) => {
    if (rule) {
      setEditingRule(rule);
      setRuleForm({
        position_id: rule.position_id || '',
        rule_name: rule.rule_name || '',
        min_education: rule.min_education || '本科',
        target_companies: safeJoin(rule.target_companies),
        target_schools: safeJoin(rule.target_schools),
        min_work_years: rule.min_work_years || 0,
        required_keywords: safeJoin(rule.required_keywords),
      });
    } else {
      setEditingRule(null);
      setRuleForm({ position_id: '', rule_name: '', min_education: '本科', target_companies: '', target_schools: '', min_work_years: 0, required_keywords: '' });
    }
    setShowRuleForm(true);
  };

  const saveRule = async () => {
    try {
      const data = {
        position_id: ruleForm.position_id || null,
        rule_name: ruleForm.rule_name,
        min_education: ruleForm.min_education,
        target_companies: splitLines(ruleForm.target_companies),
        target_schools: splitLines(ruleForm.target_schools),
        min_work_years: ruleForm.min_work_years,
        required_keywords: splitLines(ruleForm.required_keywords),
      };
      if (editingRule) {
        await updateScreeningRule(editingRule.id, data);
        toast.success('规则已更新');
      } else {
        await createScreeningRule(data);
        toast.success('规则已创建');
      }
      setShowRuleForm(false);
      const rulesData = await getScreeningRules();
      setRules(rulesData);
    } catch (err: any) { toast.error(err.response?.data?.message || '保存失败'); }
  };

  const handleDeleteRule = async (id: string) => {
    if (!window.confirm('确认删除此规则？')) return;
    try { await deleteScreeningRule(id); const rulesData = await getScreeningRules(); setRules(rulesData); toast.success('已删除'); }
    catch { toast.error('删除失败'); }
  };

  // ─── Helpers ──────────────────────────────────────────────────

  function safeJoin(val: any): string {
    if (!val) return '';
    if (typeof val === 'string') {
      try { const arr = JSON.parse(val); return Array.isArray(arr) ? arr.join('\n') : val; }
      catch { return val; }
    }
    if (Array.isArray(val)) return val.join('\n');
    return '';
  }

  function splitLines(val: string): string[] {
    return val.split(/[\n,，;；]/).map(s => s.trim()).filter(Boolean);
  }

  function parseDetail(detail: any): any {
    if (!detail) return {};
    if (typeof detail === 'string') { try { return JSON.parse(detail); } catch { return {}; } }
    return detail;
  }

  const toggleSection = (key: keyof typeof sectionsOpen) => {
    setSectionsOpen(prev => ({ ...prev, [key]: !prev[key] }));
  };

  // ─── Render ───────────────────────────────────────────────────

  if (loading) {
    return (
      <div className="h-full flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-2 border-primary-600 border-t-transparent" />
      </div>
    );
  }

  const filteredPending = (brief?.pending_pool || []).filter((c: any) =>
    !pendingSearch || c.name?.includes(pendingSearch) || c.latest_company?.includes(pendingSearch) || c.school?.includes(pendingSearch) || c.position_name?.includes(pendingSearch)
  );

  return (
    <div className="h-full flex flex-col overflow-hidden">
      {/* Header */}
      <div className="shrink-0 bg-white border-b border-gray-200 px-4 md:px-6 py-3">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-lg font-bold text-gray-800 flex items-center gap-2">
              <Zap size={20} className="text-gray-500" />
              Boss直聘简报
            </h1>
            <p className="text-sm text-gray-400 mt-0.5">自动筛选 · 规则管理 · 待筛候选人</p>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={fetchAll} className="p-2 rounded-lg text-gray-400 hover:text-primary-600 hover:bg-primary-50 transition-colors">
              <RefreshCw size={16} />
            </button>
            {notificationBell}
          </div>
        </div>

        {/* Tabs */}
        <div className="flex items-center gap-0 mt-3 -mb-3 overflow-x-auto no-scrollbar">
          {([
            { key: 'staged' as const, label: `今日推荐 (${brief?.staged_candidates?.length || 0})` },
            { key: 'overview' as const, label: '数据总览' },
            { key: 'pending' as const, label: `待筛池 (${brief?.today?.pending || 0})` },
            { key: 'rules' as const, label: '筛选规则' },
            ...(isHr ? [{ key: 'settings' as const, label: 'API设置' }] : []),
          ]).map(tab => (
            <button key={tab.key} onClick={() => setActiveTab(tab.key)}
              className={clsx('px-4 py-2 text-xs font-medium border-b-2 transition-colors whitespace-nowrap',
                activeTab === tab.key ? 'text-primary-600 border-primary-600' : 'text-gray-500 border-transparent hover:text-gray-700')}>
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-auto bg-slate-50 p-4 md:p-6 space-y-4">

        {/* ═══ STAGED TAB ═══ */}
        {activeTab === 'staged' && (() => {
          const staged = (brief?.staged_candidates || []).filter((c: any) =>
            !stagedSearch || c.name?.includes(stagedSearch) || c.latest_company?.includes(stagedSearch) || c.school?.includes(stagedSearch) || c.latest_title?.includes(stagedSearch)
          );
          // Group by job_name from screening_detail
          const byJob: Record<string, any[]> = {};
          staged.forEach((c: any) => {
            const detail = parseDetail(c.screening_detail);
            const job = detail.job_name || c.latest_title || '未分类';
            if (!byJob[job]) byJob[job] = [];
            byJob[job].push(c);
          });
          const jobGroups = Object.entries(byJob).sort((a, b) => b[1].length - a[1].length);

          return (
            <>
              {/* Action bar */}
              <div className="flex items-center gap-2 flex-wrap">
                <div className="relative flex-1 max-w-xs">
                  <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                  <input value={stagedSearch} onChange={e => setStagedSearch(e.target.value)}
                    placeholder="搜索姓名、公司、学校..."
                    className="w-full h-8 pl-8 pr-3 rounded-lg border border-gray-200 text-xs focus:outline-none focus:ring-1 focus:ring-primary-500" />
                </div>
                <span className="text-xs text-gray-400">{staged.length} 人待确认</span>
                {isHr && selectedStaged.size > 0 && (
                  <>
                    <button onClick={() => handleConfirmStaged(Array.from(selectedStaged))}
                      className="flex items-center gap-1 px-3 py-1.5 text-xs font-medium text-white bg-primary-600 hover:bg-primary-700 rounded-lg transition-colors">
                      <UserCheck size={13} />确认导入 ({selectedStaged.size})
                    </button>
                    <button onClick={() => handleDismissStaged(Array.from(selectedStaged))}
                      className="flex items-center gap-1 px-3 py-1.5 text-xs font-medium text-gray-500 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors">
                      <X size={13} />跳过 ({selectedStaged.size})
                    </button>
                  </>
                )}
              </div>

              {staged.length === 0 ? (
                <div className="bg-white rounded-xl border border-dashed border-gray-200 p-10 text-center">
                  <Zap size={40} className="mx-auto text-gray-300" />
                  <p className="mt-3 text-sm font-medium text-gray-600">暂无推荐候选人</p>
                  <p className="text-xs text-gray-400 mt-1">运行每日扫描脚本后，推荐候选人会在这里等待你确认</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {jobGroups.map(([jobName, candidates]) => (
                    <div key={jobName} className="bg-white rounded-xl border border-gray-200 overflow-hidden">
                      <div className="px-4 py-3 bg-gray-50 border-b border-gray-100 flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <span className="text-sm font-bold text-gray-800">{jobName}</span>
                          <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-primary-100 text-primary-700 font-medium">{candidates.length}人</span>
                        </div>
                        {isHr && (
                          <button onClick={() => toggleAllStaged(candidates)} className="text-[10px] text-gray-400 hover:text-primary-600">
                            {candidates.every((c: any) => selectedStaged.has(c.id)) ? '取消全选' : '全选'}
                          </button>
                        )}
                      </div>
                      <div className="divide-y divide-gray-50">
                        {candidates.map((c: any) => {
                          const detail = parseDetail(c.screening_detail);
                          const rec = detail.recommendation;
                          return (
                            <div key={c.id} className={clsx('flex items-start gap-3 px-4 py-3 hover:bg-gray-50/50 transition-colors',
                              selectedStaged.has(c.id) && 'bg-primary-50/30')}>
                              {isHr && (
                                <input type="checkbox" checked={selectedStaged.has(c.id)} onChange={() => toggleStagedSelection(c.id)}
                                  className="mt-1 rounded border-gray-300 text-primary-600 focus:ring-primary-500" />
                              )}
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2 flex-wrap">
                                  <span className="text-xs font-bold text-gray-800 cursor-pointer hover:text-primary-600"
                                    onClick={() => onOpenCandidate?.(c.id)}>{c.name}</span>
                                  {rec === 'recommended' && <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-primary-100 text-primary-700 font-medium">★ 推荐</span>}
                                  {rec === 'maybe' && <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-gray-100 text-gray-700 font-medium">可考虑</span>}
                                  {rec === 'skip' && <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-gray-100 text-gray-500 font-medium">不匹配</span>}
                                  <Stars count={c.screening_score || 0} />
                                </div>
                                <div className="flex items-center gap-3 mt-1 text-[11px] text-gray-400 flex-wrap">
                                  {c.latest_company && <span className="flex items-center gap-0.5"><Building2 size={10} />{c.latest_company}</span>}
                                  {c.education && <span className="flex items-center gap-0.5"><GraduationCap size={10} />{c.education}</span>}
                                  {c.school && <span>{c.school}</span>}
                                  {c.work_years > 0 && <span className="flex items-center gap-0.5"><Clock size={10} />{c.work_years}年</span>}
                                </div>
                                {(detail.matched_company || detail.matched_school) && (
                                  <div className="flex items-center gap-1.5 mt-1 flex-wrap">
                                    {detail.matched_company && <span className="text-[10px] px-1 py-0.5 rounded bg-primary-50 text-primary-600 border border-primary-200">✓ {detail.matched_company}</span>}
                                    {detail.matched_school && <span className="text-[10px] px-1 py-0.5 rounded bg-primary-50 text-primary-600 border border-primary-200">✓ {detail.matched_school}</span>}
                                    {detail.education_met && <span className="text-[10px] px-1 py-0.5 rounded bg-primary-50 text-primary-600 border border-primary-200">✓ 学历达标</span>}
                                  </div>
                                )}
                              </div>
                              {isHr && (
                                <div className="flex items-center gap-1 shrink-0">
                                  <button onClick={() => handleConfirmStaged([c.id])}
                                    className="p-1.5 text-primary-500 hover:bg-primary-50 rounded-lg transition-colors" title="确认导入">
                                    <UserCheck size={14} />
                                  </button>
                                  <button onClick={() => handleDismissStaged([c.id])}
                                    className="p-1.5 text-gray-400 hover:bg-gray-100 rounded-lg transition-colors" title="跳过">
                                    <X size={14} />
                                  </button>
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </>
          );
        })()}

        {/* ═══ OVERVIEW TAB ═══ */}
        {activeTab === 'overview' && brief && (
          <>
            {/* Stats cards */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <StatCard label="今日导入" value={brief.today.total} color="indigo" />
              <StatCard label="今日通过" value={brief.today.passed} color="emerald" />
              <StatCard label="今日待筛" value={brief.today.pending} color="amber" />
              <StatCard label="近7天总量" value={brief.period.total} color="slate" />
            </div>

            {/* By position */}
            <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
              <button onClick={() => toggleSection('positions')} className="w-full flex items-center justify-between px-4 py-3 text-sm font-semibold text-gray-800 hover:bg-gray-50">
                <span>按岗位分布</span>
                {sectionsOpen.positions ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
              </button>
              {sectionsOpen.positions && (
                <div className="border-t border-gray-100">
                  {(brief.by_position || []).length === 0 ? (
                    <div className="px-4 py-6 text-center text-sm text-gray-400">暂无数据</div>
                  ) : (
                    <table className="w-full text-xs">
                      <thead>
                        <tr className="bg-gray-50 text-gray-500">
                          <th className="text-left px-4 py-2 font-medium">岗位</th>
                          <th className="text-center px-2 py-2 font-medium">总计</th>
                          <th className="text-center px-2 py-2 font-medium">通过</th>
                          <th className="text-center px-2 py-2 font-medium">待筛</th>
                          <th className="text-center px-2 py-2 font-medium">通过率</th>
                        </tr>
                      </thead>
                      <tbody>
                        {brief.by_position.map((row: any, i: number) => (
                          <tr key={i} className="border-t border-gray-50 hover:bg-gray-50">
                            <td className="px-4 py-2 text-gray-700">{row.position_name || '未分配'}</td>
                            <td className="text-center px-2 py-2 text-gray-600">{row.total}</td>
                            <td className="text-center px-2 py-2 text-primary-600 font-medium">{row.passed}</td>
                            <td className="text-center px-2 py-2 text-gray-600 font-medium">{row.pending}</td>
                            <td className="text-center px-2 py-2 text-gray-500">
                              {row.total > 0 ? Math.round(row.passed / row.total * 100) + '%' : '-'}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </div>
              )}
            </div>

            {/* Recent passed */}
            <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
              <div className="px-4 py-3 text-sm font-semibold text-gray-800 border-b border-gray-100">近期通过的候选人</div>
              {(brief.recent_passed || []).length === 0 ? (
                <div className="px-4 py-6 text-center text-sm text-gray-400">暂无通过的候选人</div>
              ) : (
                <div className="divide-y divide-gray-50">
                  {brief.recent_passed.map((c: any) => (
                    <CandidateRow key={c.id} candidate={c} onOpen={onOpenCandidate} />
                  ))}
                </div>
              )}
            </div>
          </>
        )}

        {/* ═══ PENDING TAB ═══ */}
        {activeTab === 'pending' && (
          <>
            <div className="flex items-center gap-2">
              <div className="relative flex-1 max-w-xs">
                <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                <input value={pendingSearch} onChange={e => setPendingSearch(e.target.value)}
                  placeholder="搜索姓名、公司、学校..."
                  className="w-full h-8 pl-8 pr-3 rounded-lg border border-gray-200 text-xs focus:outline-none focus:ring-1 focus:ring-primary-500" />
              </div>
              <span className="text-xs text-gray-400">{filteredPending.length} 人待筛</span>
            </div>

            {filteredPending.length === 0 ? (
              <div className="bg-white rounded-xl border border-gray-200 p-8 text-center">
                <UserCheck size={40} className="mx-auto text-primary-300" />
                <p className="mt-3 text-sm text-gray-500">待筛池为空</p>
              </div>
            ) : (
              <div className="space-y-2">
                {filteredPending.map((c: any) => {
                  const detail = parseDetail(c.screening_detail);
                  return (
                    <div key={c.id} className="bg-white rounded-xl border border-gray-200 p-4">
                      <div className="flex items-start justify-between gap-3">
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="text-sm font-bold text-gray-800 cursor-pointer hover:text-primary-600"
                              onClick={() => onOpenCandidate?.(c.id)}>{c.name}</span>
                            <Stars count={c.screening_score || 0} />
                            {c.position_name && <span className="text-[10px] px-1.5 py-0.5 rounded bg-primary-50 text-primary-700">{c.position_name}</span>}
                          </div>
                          <div className="mt-1.5 flex items-center gap-3 text-xs text-gray-500 flex-wrap">
                            {c.latest_company && <span className="flex items-center gap-1"><Building2 size={11} />{c.latest_company}</span>}
                            {c.latest_title && <span>{c.latest_title}</span>}
                            {c.school && <span className="flex items-center gap-1"><GraduationCap size={11} />{c.school}</span>}
                            {c.education && <span>{c.education}</span>}
                            {c.work_years > 0 && <span className="flex items-center gap-1"><Clock size={11} />{c.work_years}年</span>}
                          </div>
                          {/* Screening detail */}
                          <div className="mt-2 flex items-center gap-2 flex-wrap">
                            {detail.education_met !== undefined && (
                              <Badge ok={detail.education_met} label="学历" />
                            )}
                            {detail.company_met !== undefined && (
                              <Badge ok={detail.company_met} label={detail.matched_company ? `名企:${detail.matched_company}` : '名企'} />
                            )}
                            {detail.school_met !== undefined && (
                              <Badge ok={detail.school_met} label={detail.matched_school ? `名校:${detail.matched_school}` : '名校'} />
                            )}
                          </div>
                        </div>
                        {isHr && (
                          <div className="flex items-center gap-1.5 shrink-0">
                            <button onClick={() => handlePromote(c.id)}
                              className="flex items-center gap-1 px-3 py-1.5 text-xs font-medium text-primary-700 bg-primary-50 hover:bg-primary-100 rounded-lg transition-colors">
                              <UserCheck size={13} />放行
                            </button>
                            <button onClick={() => handleReject(c.id)}
                              className="flex items-center gap-1 px-3 py-1.5 text-xs font-medium text-gray-600 bg-gray-50 hover:bg-gray-100 rounded-lg transition-colors">
                              <UserX size={13} />淘汰
                            </button>
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </>
        )}

        {/* ═══ RULES TAB ═══ */}
        {activeTab === 'rules' && (
          <>
            <div className="flex items-center justify-between">
              <p className="text-xs text-gray-500">通用基线：本科及以上 + 名校/名企至少满足一个。每个岗位可单独配置。</p>
              {isHr && (
                <button onClick={() => openRuleForm()} className="flex items-center gap-1 px-3 py-1.5 text-xs font-medium text-white bg-primary-600 hover:bg-primary-700 rounded-lg transition-colors">
                  <Plus size={13} />新建规则
                </button>
              )}
            </div>

            {rules.length === 0 ? (
              <div className="bg-white rounded-xl border border-dashed border-gray-200 p-8 text-center">
                <Shield size={40} className="mx-auto text-gray-300" />
                <p className="mt-3 text-sm font-medium text-gray-600">还没有筛选规则</p>
                <p className="text-xs text-gray-400 mt-1">创建通用基线规则后，Boss导入的候选人将自动筛选</p>
              </div>
            ) : (
              <div className="space-y-3">
                {rules.map(rule => (
                  <div key={rule.id} className={clsx('bg-white rounded-xl border p-4', rule.is_active ? 'border-gray-200' : 'border-gray-100 opacity-60')}>
                    <div className="flex items-start justify-between gap-3">
                      <div>
                        <div className="flex items-center gap-2">
                          <span className="text-sm font-bold text-gray-800">{rule.rule_name || '未命名规则'}</span>
                          {rule.position_id ? (
                            <span className="text-[10px] px-1.5 py-0.5 rounded bg-primary-50 text-primary-700">{rule.position_name || '指定岗位'}</span>
                          ) : (
                            <span className="text-[10px] px-1.5 py-0.5 rounded bg-gray-50 text-gray-700">通用基线</span>
                          )}
                          {!rule.is_active && <span className="text-[10px] px-1.5 py-0.5 rounded bg-gray-100 text-gray-500">已停用</span>}
                        </div>
                        <div className="mt-2 flex items-center gap-3 text-xs text-gray-500 flex-wrap">
                          <span>最低学历：{rule.min_education || '本科'}</span>
                          <span>最低年限：{rule.min_work_years || 0}年</span>
                          <span>目标公司：{countItems(rule.target_companies)}家</span>
                          <span>目标院校：{countItems(rule.target_schools)}所</span>
                          {countItems(rule.required_keywords) > 0 && <span>关键词：{countItems(rule.required_keywords)}个</span>}
                        </div>
                      </div>
                      {isHr && (
                        <div className="flex items-center gap-1 shrink-0">
                          <button onClick={() => openRuleForm(rule)} className="px-2.5 py-1.5 text-xs text-gray-500 hover:text-primary-600 hover:bg-primary-50 rounded-lg transition-colors">编辑</button>
                          <button onClick={() => handleDeleteRule(rule.id)} className="px-2.5 py-1.5 text-xs text-gray-500 hover:text-gray-600 hover:bg-gray-50 rounded-lg transition-colors">删除</button>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Rule editor modal */}
            {showRuleForm && (
              <div className="fixed inset-0 z-50 flex items-center justify-center">
                <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={() => setShowRuleForm(false)} />
                <div className="relative w-full max-w-lg mx-4 bg-white rounded-2xl shadow-2xl overflow-hidden max-h-[85vh] flex flex-col">
                  <div className="shrink-0 flex items-center justify-between px-5 py-4 border-b border-gray-100">
                    <h3 className="text-base font-bold text-gray-800">{editingRule ? '编辑筛选规则' : '新建筛选规则'}</h3>
                    <button onClick={() => setShowRuleForm(false)} className="text-gray-400 hover:text-gray-600"><X size={18} /></button>
                  </div>
                  <div className="flex-1 overflow-y-auto p-5 space-y-4">
                    <div>
                      <label className="text-xs font-medium text-gray-600 mb-1 block">规则名称</label>
                      <input value={ruleForm.rule_name} onChange={e => setRuleForm({ ...ruleForm, rule_name: e.target.value })}
                        placeholder="如：通用基线规则" className="w-full h-9 px-3 rounded-lg border border-gray-200 text-sm focus:outline-none focus:ring-1 focus:ring-primary-500" />
                    </div>
                    <div>
                      <label className="text-xs font-medium text-gray-600 mb-1 block">适用岗位</label>
                      <select value={ruleForm.position_id} onChange={e => setRuleForm({ ...ruleForm, position_id: e.target.value })}
                        className="w-full h-9 px-3 rounded-lg border border-gray-200 text-sm focus:outline-none focus:ring-1 focus:ring-primary-500">
                        <option value="">通用（所有岗位默认）</option>
                        {positions.filter(p => p.status === 'open').map(p => (
                          <option key={p.id} value={p.id}>{p.name}</option>
                        ))}
                      </select>
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="text-xs font-medium text-gray-600 mb-1 block">最低学历</label>
                        <select value={ruleForm.min_education} onChange={e => setRuleForm({ ...ruleForm, min_education: e.target.value })}
                          className="w-full h-9 px-3 rounded-lg border border-gray-200 text-sm focus:outline-none focus:ring-1 focus:ring-primary-500">
                          {(defaults?.education_levels || ['博士', '硕士', '本科', '大专']).map((e: string) => (
                            <option key={e} value={e}>{e}</option>
                          ))}
                        </select>
                      </div>
                      <div>
                        <label className="text-xs font-medium text-gray-600 mb-1 block">最低工作年限</label>
                        <input type="number" min={0} value={ruleForm.min_work_years}
                          onChange={e => setRuleForm({ ...ruleForm, min_work_years: parseInt(e.target.value) || 0 })}
                          className="w-full h-9 px-3 rounded-lg border border-gray-200 text-sm focus:outline-none focus:ring-1 focus:ring-primary-500" />
                      </div>
                    </div>
                    <div>
                      <label className="text-xs font-medium text-gray-600 mb-1 block">目标公司（每行一个）</label>
                      <textarea value={ruleForm.target_companies} onChange={e => setRuleForm({ ...ruleForm, target_companies: e.target.value })}
                        rows={4} placeholder="腾讯&#10;字节跳动&#10;华为&#10;..."
                        className="w-full px-3 py-2 rounded-lg border border-gray-200 text-sm focus:outline-none focus:ring-1 focus:ring-primary-500 resize-none" />
                      <p className="text-[10px] text-gray-400 mt-1">留空则使用系统默认名企列表（{defaults?.top_companies?.length || 0}家）</p>
                    </div>
                    <div>
                      <label className="text-xs font-medium text-gray-600 mb-1 block">目标院校（每行一个）</label>
                      <textarea value={ruleForm.target_schools} onChange={e => setRuleForm({ ...ruleForm, target_schools: e.target.value })}
                        rows={4} placeholder="清华大学&#10;北京大学&#10;985&#10;..."
                        className="w-full px-3 py-2 rounded-lg border border-gray-200 text-sm focus:outline-none focus:ring-1 focus:ring-primary-500 resize-none" />
                      <p className="text-[10px] text-gray-400 mt-1">留空则使用系统默认名校列表（{defaults?.top_schools?.length || 0}所）</p>
                    </div>
                    <div>
                      <label className="text-xs font-medium text-gray-600 mb-1 block">技能关键词（每行一个，可选）</label>
                      <textarea value={ruleForm.required_keywords} onChange={e => setRuleForm({ ...ruleForm, required_keywords: e.target.value })}
                        rows={2} placeholder="Java&#10;React&#10;..."
                        className="w-full px-3 py-2 rounded-lg border border-gray-200 text-sm focus:outline-none focus:ring-1 focus:ring-primary-500 resize-none" />
                    </div>
                  </div>
                  <div className="shrink-0 flex items-center justify-end gap-2 px-5 py-3 border-t border-gray-100">
                    <button onClick={() => setShowRuleForm(false)} className="px-4 py-2 text-xs text-gray-500 hover:bg-gray-100 rounded-lg transition-colors">取消</button>
                    <button onClick={saveRule} className="flex items-center gap-1 px-4 py-2 text-xs font-medium text-white bg-primary-600 hover:bg-primary-700 rounded-lg transition-colors">
                      <Check size={13} />保存规则
                    </button>
                  </div>
                </div>
              </div>
            )}
          </>
        )}

        {/* ═══ SETTINGS TAB ═══ */}
        {activeTab === 'settings' && isHr && (
          <>
            {/* 邮箱自动初筛 — 自助管理自己的投递邮箱 */}
            <MailAccounts currentUser={currentUser} />

            <div className="bg-white rounded-xl border border-gray-200 p-4">
              <h3 className="text-sm font-bold text-gray-800 flex items-center gap-2"><Key size={15} />API 密钥管理</h3>
              <p className="text-xs text-gray-500 mt-1">创建 API Key 后，boss-daily-brief skill 即可调用导入接口。</p>

              <div className="mt-4 flex items-center gap-2">
                <input value={newKeyName} onChange={e => setNewKeyName(e.target.value)}
                  placeholder="密钥名称（可选）"
                  className="flex-1 h-8 px-3 rounded-lg border border-gray-200 text-xs focus:outline-none focus:ring-1 focus:ring-primary-500" />
                <button onClick={handleCreateApiKey}
                  className="flex items-center gap-1 px-3 py-1.5 text-xs font-medium text-white bg-primary-600 hover:bg-primary-700 rounded-lg transition-colors">
                  <Plus size={13} />生成密钥
                </button>
              </div>

              {generatedKey && (
                <div className="mt-3 p-3 bg-primary-50 rounded-lg border border-primary-200">
                  <p className="text-xs font-medium text-primary-800 mb-1">⚠️ 请立即复制，关闭后无法再查看</p>
                  <div className="flex items-center gap-2">
                    <code className="flex-1 text-xs font-mono text-primary-700 bg-white px-2 py-1 rounded break-all">{generatedKey}</code>
                    <button onClick={() => { navigator.clipboard.writeText(generatedKey); toast.success('已复制'); }}
                      className="shrink-0 p-1.5 text-primary-600 hover:bg-primary-100 rounded"><Copy size={14} /></button>
                  </div>
                </div>
              )}

              {apiKeys.length > 0 && (
                <div className="mt-4 space-y-2">
                  {apiKeys.map(k => (
                    <div key={k.id} className="flex items-center justify-between px-3 py-2 bg-gray-50 rounded-lg">
                      <div>
                        <span className="text-xs font-medium text-gray-700">{k.name || '未命名'}</span>
                        <span className="ml-2 text-[10px] text-gray-400">
                          {k.last_used_at ? `上次使用: ${new Date(k.last_used_at).toLocaleDateString()}` : '未使用'}
                        </span>
                      </div>
                      <button onClick={() => handleDeleteApiKey(k.id)}
                        className="text-xs text-gray-400 hover:text-gray-600 transition-colors">禁用</button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Integration guide */}
            <div className="bg-white rounded-xl border border-gray-200 p-4">
              <h3 className="text-sm font-bold text-gray-800">对接指南</h3>
              <div className="mt-3 text-xs text-gray-600 space-y-2 font-mono bg-gray-50 rounded-lg p-3">
                <p className="text-gray-400"># boss-daily-brief skill 调用示例</p>
                <p>POST {window.location.origin}/api/screening/import</p>
                <p>Header: X-Api-Key: sk_your_key_here</p>
                <p>Body: {'{'}</p>
                <p className="pl-4">"platform": "boss",</p>
                <p className="pl-4">"position_id": "岗位ID（可选）",</p>
                <p className="pl-4">"items": [{'{'}</p>
                <p className="pl-8">"name": "张三",</p>
                <p className="pl-8">"phone": "138xxxx",</p>
                <p className="pl-8">"latest_company": "腾讯",</p>
                <p className="pl-8">"education": "本科",</p>
                <p className="pl-8">"school": "清华大学",</p>
                <p className="pl-8">"work_years": 3,</p>
                <p className="pl-8">...</p>
                <p className="pl-4">{'}'}]</p>
                <p>{'}'}</p>
              </div>
            </div>
            {/* Headhunter token management */}
            <div className="bg-white rounded-xl border border-gray-200 p-4">
              <h3 className="text-sm font-bold text-gray-800 flex items-center gap-2"><Key size={15} />猎头查重链接</h3>
              <p className="text-xs text-gray-500 mt-1">生成专属链接给猎头，打开即可查重（每链接每天限200次）</p>

              <div className="mt-4 flex items-center gap-2">
                <input value={newHhName} onChange={e => setNewHhName(e.target.value)}
                  placeholder="猎头/机构名称"
                  className="flex-1 h-8 px-3 rounded-lg border border-gray-200 text-xs focus:outline-none focus:ring-1 focus:ring-primary-500" />
                <button onClick={handleCreateHhToken}
                  className="flex items-center gap-1 px-3 py-1.5 text-xs font-medium text-white bg-primary-600 hover:bg-primary-700 rounded-lg transition-colors">
                  <Plus size={13} />生成链接
                </button>
              </div>

              {newHhLink && (
                <div className="mt-3 p-3 bg-primary-50 rounded-lg border border-primary-200">
                  <p className="text-xs text-primary-700 font-medium mb-1">新链接已生成，发送给猎头：</p>
                  <div className="flex items-center gap-2">
                    <code className="flex-1 text-xs bg-white px-2 py-1 rounded border border-primary-300 break-all">{newHhLink}</code>
                    <button onClick={() => { navigator.clipboard.writeText(newHhLink); toast.success('已复制'); }}
                      className="text-xs text-primary-700 hover:text-primary-900"><Copy size={14} /></button>
                  </div>
                </div>
              )}

              {hhTokens.length > 0 && (
                <div className="mt-4 space-y-2">
                  {hhTokens.map(t => (
                    <div key={t.id} className="flex items-center justify-between px-3 py-2 bg-gray-50 rounded-lg">
                      <div>
                        <span className="text-xs font-medium text-gray-700">{t.name}</span>
                        <span className="text-[10px] text-gray-400 ml-2">
                          {t.last_used_at ? `最近: ${new Date(t.last_used_at).toLocaleDateString()}` : '未使用'}
                        </span>
                      </div>
                      <div className="flex items-center gap-2">
                        <button onClick={() => { navigator.clipboard.writeText(`${window.location.origin}/headhunter/${t.token}`); toast.success('链接已复制'); }}
                          className="text-xs text-primary-500 hover:text-primary-700"><Copy size={13} /></button>
                        <button onClick={() => handleDeleteHhToken(t.id)}
                          className="text-xs text-gray-400 hover:text-gray-600 transition-colors">停用</button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
}

// ─── Sub-components ─────────────────────────────────────────────

function StatCard({ label, value, color }: { label: string; value: number; color: string }) {
  const colors: Record<string, string> = {
    indigo: 'bg-primary-50 text-primary-700 border-primary-100',
    emerald: 'bg-primary-50 text-primary-700 border-primary-100',
    amber: 'bg-gray-50 text-gray-700 border-gray-100',
    slate: 'bg-slate-50 text-slate-700 border-slate-100',
  };
  return (
    <div className={clsx('rounded-xl border p-4', colors[color] || colors.slate)}>
      <div className="text-xl font-bold">{value}</div>
      <div className="text-xs mt-1 opacity-70">{label}</div>
    </div>
  );
}

function Stars({ count }: { count: number }) {
  return (
    <span className="flex items-center gap-0">
      {[1, 2, 3, 4, 5].map(i => (
        <Star key={i} size={11} className={i <= count ? 'text-gray-400 fill-gray-400' : 'text-gray-200'} />
      ))}
    </span>
  );
}

function Badge({ ok, label }: { ok: boolean; label: string }) {
  return (
    <span className={clsx('text-[10px] px-1.5 py-0.5 rounded border font-medium',
      ok ? 'bg-primary-50 text-primary-700 border-primary-200' : 'bg-gray-50 text-gray-400 border-gray-200')}>
      {ok ? '✓' : '✗'} {label}
    </span>
  );
}

function CandidateRow({ candidate: c, onOpen }: { candidate: any; onOpen?: (id: string) => void }) {
  let detail: any = {};
  try { detail = typeof c.screening_detail === 'string' ? JSON.parse(c.screening_detail) : (c.screening_detail || {}); } catch {}
  return (
    <div className="flex items-center justify-between px-4 py-3 hover:bg-gray-50 transition-colors">
      <div className="flex items-center gap-3 min-w-0 flex-1">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-xs font-bold text-gray-800 cursor-pointer hover:text-primary-600"
              onClick={() => onOpen?.(c.id)}>{c.name}</span>
            {c.latest_title && <span className="text-[10px] px-1.5 py-0.5 rounded bg-primary-50 text-primary-600">{c.latest_title}</span>}
            {c.education && <span className="text-[10px] px-1.5 py-0.5 rounded bg-primary-50 text-primary-600">{c.education}</span>}
          </div>
          <div className="flex items-center gap-3 mt-1 text-[11px] text-gray-400 flex-wrap">
            {c.latest_company && <span className="flex items-center gap-0.5"><Building2 size={10} />{c.latest_company}</span>}
            {c.school && <span className="flex items-center gap-0.5"><GraduationCap size={10} />{c.school}</span>}
            {c.work_years > 0 && <span className="flex items-center gap-0.5"><Clock size={10} />{c.work_years}年</span>}
          </div>
          {(detail.matched_company || detail.matched_school) && (
            <div className="flex items-center gap-1.5 mt-1 flex-wrap">
              {detail.matched_company && <span className="text-[10px] px-1 py-0.5 rounded bg-primary-50 text-primary-600 border border-primary-200">✓ {detail.matched_company}</span>}
              {detail.matched_school && <span className="text-[10px] px-1 py-0.5 rounded bg-primary-50 text-primary-600 border border-primary-200">✓ {detail.matched_school}</span>}
            </div>
          )}
        </div>
      </div>
      <div className="flex items-center gap-2 shrink-0">
        <Stars count={c.screening_score || 0} />
      </div>
    </div>
  );
}

function countItems(val: any): number {
  if (!val) return 0;
  if (Array.isArray(val)) return val.length;
  if (typeof val === 'string') {
    try { const arr = JSON.parse(val); return Array.isArray(arr) ? arr.length : 0; }
    catch { return 0; }
  }
  return 0;
}
